import { Button, buttonVariants } from '@playbooks/ui/components/button'
import { Tooltip, TooltipTrigger } from '@playbooks/ui/components/tooltip'
import type { VariantProps } from 'class-variance-authority'
import { useAppContext } from '../../contexts/AppContext'
import useMountEffect from '../../hooks/useMountEffect'
import { useState, RefObject } from 'react'
import { TooltipContentWithContainer } from './TooltipContentWithContainer'
import { cn } from '@playbooks/ui/lib/utils'

/**
 * Custom Button based on Shadcn button component and supports loading state and tooltip
 */
export function CustomButton(
  props: React.ComponentProps<'button'> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean
      isLoading?: boolean
      tooltip?: React.ReactNode
    },
) {
  const {
    rootRef,
    settings: { accentColor },
    isAccentColorDark,
  } = useAppContext()
  const [ref, setRef] = useState<RefObject<HTMLDivElement | null>>(null!)

  useMountEffect(() => setRef(rootRef))

  const { children, isLoading, tooltip, size, className, ...restProps } = props
  const isIconButton = size === 'icon' || size === 'icon-lg'

  return !ref ? null : (
    <Tooltip delayDuration={500}>
      <TooltipTrigger asChild>
        <Button
          className={cn(
            'max-w-full',
            accentColor && 'bg-(--color-ac-accent) text-white',
            isAccentColorDark && 'border-zinc-800 border-px',
            className,
          )}
          size={size}
          {...restProps}
        >
          {isLoading ? (
            'Loading...'
          ) : isIconButton ? (
            children
          ) : (
            <div className="w-full overflow-hidden text-ellipsis whitespace-nowrap">{children}</div>
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContentWithContainer container={ref.current} className={!tooltip ? 'hidden' : ''}>
        <p>{tooltip}</p>
      </TooltipContentWithContainer>
    </Tooltip>
  )
}
